import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Menu, LogOut, User, LayoutDashboard } from "lucide-react";
import { Logo } from "@/components/common/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navItems = [
  { to: "/", label: "Início" },
  { to: "/servicos", label: "Serviços" },
  { to: "/galeria", label: "Galeria" },
  { to: "/agendar", label: "Agendar" },
];

export const Header = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-sky-500/10 bg-slate-950/80 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <Logo size={40} className="rounded-full shadow-lg shadow-sky-500/20" />
          <div className="flex flex-col leading-none">
            <span className="text-base font-black tracking-tight text-white">TSBRASIL</span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-sky-400">Ar-Condicionado</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                location.pathname === item.to
                  ? "text-sky-400 bg-sky-500/10"
                  : "text-slate-300 hover:text-white hover:bg-slate-800/50"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {!user ? (
            <>
              <Button
                variant="ghost"
                onClick={() => nav("/entrar")}
                className="hidden sm:inline-flex text-slate-200 hover:text-white hover:bg-slate-800"
                data-testid="header-login-btn"
              >
                Entrar
              </Button>
              <Button
                onClick={() => nav("/cadastro")}
                className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold btn-shine"
                data-testid="header-register-btn"
              >
                Cadastrar
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 text-slate-200 hover:bg-slate-800" data-testid="user-menu-trigger">
                  <div className="h-7 w-7 rounded-full bg-sky-500/20 flex items-center justify-center text-sky-400 text-sm font-bold">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">{user.name?.split(" ")[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                <DropdownMenuItem onClick={() => nav("/painel")} className="gap-2 cursor-pointer" data-testid="menu-dashboard">
                  <LayoutDashboard className="h-4 w-4" /> Meu Painel
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => nav("/admin")} className="gap-2 cursor-pointer text-sky-400" data-testid="menu-admin">
                    <User className="h-4 w-4" /> Administração
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem onClick={onLogout} className="gap-2 cursor-pointer text-red-400" data-testid="menu-logout">
                  <LogOut className="h-4 w-4" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden text-slate-200" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 md:hidden">
              {navItems.map((item) => (
                <DropdownMenuItem key={item.to} onClick={() => nav(item.to)} className="cursor-pointer">
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
